import { Controller } from "@hotwired/stimulus"
import { getI18nMessage } from "../lib/i18n_helper"

// Image Upload Controller
// File upload with preview and validation
//
// Features:
// - Multiple file upload support
// - Image preview thumbnails
// - File size validation (max 5MB per image)
// - File count limit (max 5 images)
// - File type validation (images only)
// - Remove preview capability
// - Drag & drop support (optional enhancement)
// - Accessible with keyboard navigation
//
// Usage (controller on wrapper div):
// <div data-controller="image-upload"
//      data-image-upload-max-value="5"
//      data-image-upload-max-size-value="5242880"
//      data-i18n-errors-max-files="最多只能上傳 %{count} 張照片"
//      data-i18n-errors-not-image="%{name} 不是圖片檔案"
//      data-i18n-errors-file-size="%{name} 超過 %{size}MB 限制"
//      data-i18n-label-remove="移除 %{name}">
//   <input type="file" multiple accept="image/*"
//     data-image-upload-target="input"
//     data-action="change->image-upload#preview">
//   <div data-image-upload-target="preview"></div>
//   <p data-image-upload-target="counter"></p>  <!-- optional -->
// </div>
export default class extends Controller {
  static targets = ["preview", "input", "counter"]
  static values = {
    max: { type: Number, default: 5 },
    // Default fallback: BusinessConstants::IMAGE_UPLOAD_MAX_SIZE (5MB)
    // Always pass via data-image-upload-max-size-value for SSOT compliance
    maxSize: { type: Number, default: 5 * 1024 * 1024 }
  }

  connect() {
    this.files = []
    this._updateCounter()
  }

  // Preview selected images (accumulates files across multiple selections)
  preview(event) {
    const newFiles = Array.from(event.target.files)

    // Validate total file count (existing + new)
    if (this.files.length + newFiles.length > this.maxValue) {
      const remaining = this.maxValue - this.files.length
      const errorMsg = getI18nMessage(
        this.element,
        'errorsMaxFiles',
        `最多只能上傳 ${this.maxValue} 張照片（還可選 ${remaining} 張）`,
        { count: this.maxValue }
      )
      this.showError(errorMsg)
      this.inputTarget.value = ""
      return
    }

    // Validate each file
    const validFiles = newFiles.filter(file => this.validateFile(file))

    if (validFiles.length === 0) {
      this.inputTarget.value = ""
      return
    }

    // Append valid files to existing collection
    const startIndex = this.files.length
    this.files = this.files.concat(validFiles)

    // Generate previews for new files only (preserve existing)
    validFiles.forEach((file, i) => {
      this.createPreview(file, startIndex + i)
    })

    // Sync input FileList with accumulated files
    this.updateInputFiles()
    this._updateCounter()
  }

  // Validate individual file
  validateFile(file) {
    // Check file type
    if (!file.type.startsWith("image/")) {
      const errorMsg = getI18nMessage(
        this.element,
        'errorsNotImage',
        `${file.name} 不是圖片檔案`,
        { name: file.name }
      )
      this.showError(errorMsg)
      return false
    }

    // Check file size
    if (file.size > this.maxSizeValue) {
      const maxSizeMB = (this.maxSizeValue / 1024 / 1024).toFixed(1)
      const errorMsg = getI18nMessage(
        this.element,
        'errorsFileSize',
        `${file.name} 超過 ${maxSizeMB}MB 限制`,
        { name: file.name, size: maxSizeMB }
      )
      this.showError(errorMsg)
      return false
    }

    return true
  }

  // Create preview thumbnail
  createPreview(file, index) {
    const reader = new FileReader()

    reader.onload = (e) => {
      const previewItem = this.createPreviewElement(e.target.result, file.name, index)
      this.previewTarget.appendChild(previewItem)
    }

    reader.readAsDataURL(file)
  }

  // Create preview DOM element
  createPreviewElement(src, fileName, index) {
    const removeLabel = getI18nMessage(
      this.element,
      'labelRemove',
      `移除 ${fileName}`,
      { name: fileName }
    )

    const div = document.createElement("div")
    div.className = "relative group"
    div.dataset.index = index

    div.innerHTML = `
      <img src="${src}"
           alt="${fileName}"
           class="h-24 w-24 rounded-lg object-cover border border-charcoal-300">

      <button type="button"
              class="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 active:scale-[0.97] transition-all duration-150 flex items-center justify-center"
              data-action="click->image-upload#remove"
              data-index="${index}"
              aria-label="${removeLabel}">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      <p class="mt-1 text-xs text-charcoal-600 truncate" title="${fileName}">${fileName}</p>
    `

    return div
  }

  // Remove preview and file
  remove(event) {
    const button = event.currentTarget
    const index = parseInt(button.dataset.index)

    // Remove from files array
    this.files.splice(index, 1)

    // Remove preview element
    const previewItem = button.closest("[data-index]")
    previewItem.remove()

    // Re-index remaining previews to keep data-index in sync with files array
    this.previewTarget.querySelectorAll("[data-index]").forEach((el, i) => {
      el.dataset.index = i
      const btn = el.querySelector("button[data-index]")
      if (btn) btn.dataset.index = i
    })

    // Update input files (create new FileList)
    this.updateInputFiles()

    // If no files left, clear input
    if (this.files.length === 0) {
      this.inputTarget.value = ""
    }

    this._updateCounter()
  }

  // Update input files from array
  updateInputFiles() {
    // Create new DataTransfer to build FileList
    const dataTransfer = new DataTransfer()

    this.files.forEach(file => {
      dataTransfer.items.add(file)
    })

    // Update input files
    this.inputTarget.files = dataTransfer.files
  }

  // Clear all previews
  clearPreviews() {
    this.previewTarget.innerHTML = ""
  }

  // Show validation error
  showError(message) {
    // Find or create error message element within controller scope
    let errorElement = this.element.querySelector(".error-message")

    if (!errorElement) {
      errorElement = document.createElement("p")
      errorElement.className = "error-message text-sm text-red-600 mt-1"
      this.element.appendChild(errorElement)
    }

    errorElement.textContent = message

    // Clear error after duration (BusinessConstants::UITiming::TOAST_DURATION_MS)
    setTimeout(() => {
      errorElement.textContent = ""
    }, window.BusinessConstants.UITiming.TOAST_DURATION_MS)
  }

  // Update file count display (optional target — safe for consumers without counter)
  _updateCounter() {
    if (!this.hasCounterTarget) return
    this.counterTarget.textContent = `${this.files.length}/${this.maxValue}`
  }

  // Optional: Drag & drop support
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.add("border-crust-500", "bg-crust-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.remove("border-crust-500", "bg-crust-50")
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.remove("border-crust-500", "bg-crust-50")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      // Manually trigger preview with dropped files
      this.inputTarget.files = files
      this.preview({ target: this.inputTarget })
    }
  }
}
